<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import {
    ClockIcon,
    ViewColumnsIcon,
    Cog6ToothIcon,
    ArrowLeftOnRectangleIcon,
    EllipsisVerticalIcon,
} from '@heroicons/vue/24/solid'
import { useAuthStore } from '@src/stores/auth'
import useStore from '@src/stores/store'
import { useChatSessionStore } from '@src/stores/chatSession'
import RobotIcon from "@src/components/ui/icons/RobotIcon.vue";

const store = useStore()
const authStore = useAuthStore()
const sessionStore = useChatSessionStore()

const open = ref(false)
const confirmLogout = ref(false)

const isActive = computed(() =>
    ['bot', 'schedule-messages', 'kanban'].includes(store.activeSidebarComponent)
)

function close() {
    open.value = false
    confirmLogout.value = false
}

const navExtra = [
    { key: 'bot', label: 'Fluxo', icon: RobotIcon },
    { key: 'schedule-messages', label: 'Mensagens Agendadas', icon: ClockIcon },
    { key: 'kanban', label: 'Kanban', icon: ViewColumnsIcon },
]

function pick(key: string) {
    close()
    sessionStore.actionSessions = []
    sessionStore.openActionsBulk = false
    store.activeSidebarComponent = key
}

function onSettings() {
    close()
    store.activeSidebarComponent = 'settings'
}

function askLogout() {
    confirmLogout.value = true
}

function cancelLogout() {
    confirmLogout.value = false
}

async function confirmAndExit() {
    close()
    try {
        await authStore.logout()
    } catch (e) {
        console.error(e)
    } finally {
        store.goToLogin()
        sessionStore.clearSession()
        indexedDB.deleteDatabase('dChatDatabase')
    }
}

function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') close()
}

onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<template>
    <!-- trigger: same structure as MenuItem for visual consistency -->
    <div class="cursor-pointer" @click="open = true">
        <div class="group xs:mb-0 md:mb-4 xl:mb-6 flex items-center flex-col">
            <button
                type="button"
                aria-label="Mais opções"
                title="Mais"
                class="relative focus:outline-none cursor-pointer"
            >
                <EllipsisVerticalIcon
                    :class="isActive || open ? ['text-brand-300', 'dark:text-white'] : ['text-gray-300']"
                    class="w-8 h-7 group-focus:text-brand-300 group-hover:text-brand-400 active:text-brand-300 active:scale-110 dark:text-gray-500 transition ease-out duration-200"
                />
            </button>
            <p class="m-0 text-xs text-gray-700 text-center dark:text-gray-200 max-w-[80px] truncate inline-block">Mais</p>
        </div>
    </div>

    <Teleport to="body">
        <!-- scrim -->
        <Transition
            enter-active-class="transition-opacity duration-200"
            leave-active-class="transition-opacity duration-200"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
        >
            <div v-if="open" class="fixed inset-0 z-40 bg-gray-900/50" @click="close" />
        </Transition>

        <!-- bottom sheet -->
        <Transition
            enter-active-class="transition-transform duration-300 ease-out"
            leave-active-class="transition-transform duration-200 ease-in"
            enter-from-class="translate-y-full"
            leave-to-class="translate-y-full"
        >
            <div
                v-if="open"
                class="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl bg-white dark:bg-gray-800 pb-5 shadow-2xl shadow-black/30"
                role="dialog"
                aria-modal="true"
                aria-label="Mais opções"
            >
                <!-- handle -->
                <div class="mx-auto mt-3 h-1 w-9 rounded-full bg-gray-200 dark:bg-white/15" />

                <!-- navigation group -->
                <p class="px-5 pb-3 pt-3 text-xs font-bold uppercase tracking-wide text-gray-400">Navegação</p>
                <div class="grid grid-cols-3 gap-1.5 px-3.5">
                    <button
                        v-for="item in navExtra"
                        :key="item.key"
                        type="button"
                        @click="pick(item.key)"
                        class="flex flex-col items-center gap-2.5 rounded-2xl bg-gray-50 dark:bg-white/5 px-2 py-5
                               transition hover:bg-gray-100 dark:hover:bg-white/10 active:bg-gray-200 dark:active:bg-white/15"
                    >
                        <span class="grid h-11 w-11 place-items-center rounded-xl bg-brand-500/10 dark:bg-brand-500/15 text-brand-500 dark:text-brand-400">
                            <component :is="item.icon" class="h-5 w-5" />
                        </span>
                        <span class="text-center text-xs font-medium leading-tight text-gray-800 dark:text-gray-100">{{ item.label }}</span>
                    </button>
                </div>

                <!-- divider -->
                <div class="mx-5 my-5 h-px bg-gray-100 dark:bg-white/10" />

                <!-- account group -->
                <p class="px-5 pb-2 text-xs font-bold uppercase tracking-wide text-gray-400">Conta</p>

                <button
                    type="button"
                    @click="onSettings"
                    class="flex w-full items-center gap-4 px-5 py-3 text-left text-sm font-medium
                           text-gray-700 dark:text-gray-100
                           transition hover:bg-gray-50 dark:hover:bg-white/5 active:bg-gray-100 dark:active:bg-white/10"
                >
                    <Cog6ToothIcon class="h-5 w-5 text-gray-400 flex-shrink-0" />
                    Configurações
                </button>

                <button
                    type="button"
                    @click="askLogout"
                    class="flex w-full items-center gap-4 px-5 py-3 text-left text-sm font-medium
                           text-red-500 dark:text-red-400
                           transition hover:bg-red-50 dark:hover:bg-red-500/10 active:bg-red-100 dark:active:bg-red-500/20"
                >
                    <ArrowLeftOnRectangleIcon class="h-5 w-5 flex-shrink-0" />
                    Sair
                </button>
            </div>
        </Transition>

        <!-- logout confirmation dialog -->
        <Transition
            enter-active-class="transition-opacity duration-200"
            leave-active-class="transition-opacity duration-150"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
        >
            <div v-if="confirmLogout" class="fixed inset-0 z-[60] flex items-center justify-center p-5">
                <div class="absolute inset-0 bg-gray-900/60" @click="cancelLogout" />
                <div class="relative w-full max-w-xs rounded-2xl bg-white dark:bg-gray-800 p-5 text-center shadow-xl shadow-black/30">
                    <div class="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-red-50 dark:bg-red-500/15 text-red-500 dark:text-red-400">
                        <ArrowLeftOnRectangleIcon class="h-6 w-6" />
                    </div>
                    <h3 class="text-base font-bold text-gray-900 dark:text-white">Sair da conta?</h3>
                    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Você precisará entrar novamente para acessar seus atendimentos.
                    </p>
                    <div class="mt-5 flex gap-3">
                        <button
                            type="button"
                            @click="cancelLogout"
                            class="flex-1 rounded-xl bg-gray-100 dark:bg-white/10 py-3 text-sm font-semibold
                                   text-gray-700 dark:text-white
                                   transition hover:bg-gray-200 dark:hover:bg-white/15 active:bg-gray-300 dark:active:bg-white/20"
                        >Cancelar</button>
                        <button
                            type="button"
                            @click="confirmAndExit"
                            class="flex-1 rounded-xl bg-red-500 py-3 text-sm font-semibold text-white
                                   transition hover:bg-red-600 active:bg-red-700"
                        >Sair</button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>